import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import { EcoInventDataset } from "@/hooks/useEcoInventDatasets";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface EolMatch {
  id: number;
  name?: string;
}
export interface ProductEol {
  id: number;
  external_id: string;
  name: string;
  category: string | null;
  match?: EolMatch;
  relation: "primary_source";
  status: "complete" | "incomplete";
}

export interface EolDetail extends ProductEol {
  proxy_id?: number;
  match?: EcoInventDataset;
}

export function useGetProductEol({
  pagination,
  filter,
  search,
  sort,
  year,
  showUnrelated = false,
  enabled,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  year?: number;
  showUnrelated?: boolean;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["products", "eol", "list", year, showUnrelated, pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<ProductEol>>("/products/eol", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            year: year,
            show_unrelated: showUnrelated,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export function usePostEndOfLife() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productIds, match_id }: { productIds: number[]; match_id: number }) =>
      axios.post(
        "/products/eol",
        {},
        {
          params: {
            ids: productIds.join(";"),
            match_id: match_id,
          },
        }
      ),
    onSuccess: (_) => {
      smartQueryClient.invalidateQueries(["products"]);
    },
  });
}

export function useDeleteEol() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productIds }: { productIds: number[] }) =>
      axios.delete("/products/eol", {
        params: {
          ids: productIds.join(";"),
        },
      }),
    onMutate: (variables: Record<string, number[]>) => {
      variables.productIds.forEach((id: number) => {
        smartQueryClient.setQueryData(["products", "eol", id], (data: ProductEol | undefined) => {
          if (data) {
            return { ...data, match: undefined };
          }
        });
      });
    },
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["products"]);
    },
  });
}

export function useGetEol({ productId, enabled }: { productId: number; enabled?: boolean }) {
  return useQuery({
    queryKey: ["products", "eol", productId],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios.get<EolDetail>("/products/" + productId + "/eol").then((result) => result.data),
  });
}

export function useProductEolStatusPerYear() {
  return useQuery({
    queryKey: ["products", "eol", "years"],
    keepPreviousData: true,
    queryFn: () => axios.get<YearStatus[]>("/products/eol/years", {}).then((result) => result.data),
  });
}
